<template>
  <div class="image-uploader">
    <v-subheader class="px-0">
      {{ label }}
    </v-subheader>

    <div class="upload-area">
      <!-- プレビュー表示 -->
      <div
        v-if="previewSrc"
        class="preview-container"
      >
        <v-img
          :src="previewSrc"
          max-width="200"
          max-height="200"
          contain
          class="preview-image"
        />
        <v-btn
          icon
          small
          class="remove-btn"
          color="error"
          @click="handleRemove"
        >
          <v-icon small>
            mdi-close
          </v-icon>
        </v-btn>
      </div>

      <!-- ファイル選択 -->
      <v-file-input
        v-model="localFile"
        :label="previewSrc ? '画像を変更' : '画像を選択'"
        accept="image/png,image/jpeg,image/gif"
        prepend-icon="mdi-camera"
        :hint="hint"
        persistent-hint
        outlined
        dense
        :rules="fileRules"
        @change="handleFileChange"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'

export default {
  name: 'ImageUploader',

  props: {
    value: {
      type: File,
      default: null,
    },
    label: {
      type: String,
      default: '画像',
    },
    hint: {
      type: String,
      default: '対応形式: PNG, JPG, GIF / 上限: 1MB',
    },
    previewUrl: {
      type: String,
      default: null,
    },
    maxSize: {
      type: Number,
      default: 1024 * 1024, // 1MB
    },
  },

  setup(props, { emit }) {
    const localFile = ref(null)
    const localPreviewUrl = ref(null)

    // ファイルバリデーションルール
    const fileRules = [
      v => !v || v.size <= props.maxSize || 'ファイルサイズは1MB以下にしてください',
      v => !v || ['image/png', 'image/jpeg', 'image/gif'].includes(v.type) || '対応していないファイル形式です',
    ]

    // プレビュー用URL
    const previewSrc = computed(() => {
      if (localPreviewUrl.value) {
        return localPreviewUrl.value
      }

      return props.previewUrl
    })

    // ファイル変更ハンドラ
    const handleFileChange = file => {
      if (file) {
        // バリデーション
        if (file.size > props.maxSize) {
          return
        }
        if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
          return
        }

        // ローカルプレビュー
        if (localPreviewUrl.value) {
          URL.revokeObjectURL(localPreviewUrl.value)
        }
        localPreviewUrl.value = URL.createObjectURL(file)
        emit('input', file)
      } else {
        if (localPreviewUrl.value) {
          URL.revokeObjectURL(localPreviewUrl.value)
        }
        localPreviewUrl.value = null
        emit('input', null)
      }
    }

    // 画像削除ハンドラ
    const handleRemove = () => {
      localFile.value = null
      if (localPreviewUrl.value) {
        URL.revokeObjectURL(localPreviewUrl.value)
      }
      localPreviewUrl.value = null
      emit('input', null)
      emit('remove')
    }

    // propsの変更を監視
    watch(
      () => props.value,
      newVal => {
        if (!newVal) {
          localFile.value = null
          if (localPreviewUrl.value) {
            URL.revokeObjectURL(localPreviewUrl.value)
          }
          localPreviewUrl.value = null
        }
      },
    )

    return {
      localFile,
      localPreviewUrl,
      fileRules,
      previewSrc,
      handleFileChange,
      handleRemove,
    }
  },
}
</script>

<style scoped>
.image-uploader {
  margin-bottom: 16px;
}

.upload-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-container {
  position: relative;
  display: inline-block;
}

.preview-image {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.remove-btn {
  position: absolute;
  top: -8px;
  right: -8px;
}
</style>
